"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  INDUSTRY_TEMPLATES,
  type CompanyProfile,
  type FinanceRole,
  type IndustryTemplate,
  type NearbyLeadResponse,
  type RealEstateRole,
  type RegisterWithIdResponse,
  type SignInResponse,
  type ValidateUniqueIdResponse,
} from "@virtuous/shared";
import { BrandLogo } from "@/components/BrandLogo";
import { apiFetch } from "@/lib/api";
import { saveSession } from "@/lib/session";

type Path = "choose" | "has_id" | "no_id" | "returning";
type Step = 1 | 2;

/**
 * Gate: Unique ID from a real referring person, then your info.
 * No ID → nearby lead only (no network access until meet + admin ID).
 */
export default function EnterPage() {
  const router = useRouter();
  const [path, setPath] = useState<Path>("choose");
  const [step, setStep] = useState<Step>(1);

  // Part 1 — Unique ID
  const [uniqueId, setUniqueId] = useState("");
  const [idCheck, setIdCheck] = useState<ValidateUniqueIdResponse | null>(null);
  const [checking, setChecking] = useState(false);

  // Part 2 — company first, then person + location (for customer-facing referrals)
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [password, setPassword] = useState("");
  const [industry, setIndustry] = useState<IndustryTemplate>("general");
  const [companyName, setCompanyName] = useState("");
  const [website, setWebsite] = useState("");
  const [calendarLink, setCalendarLink] = useState("");
  const [availabilityNotes, setAvailabilityNotes] = useState("");
  const [preferences, setPreferences] = useState("");
  // Real estate specifics
  const [reRole, setReRole] = useState<RealEstateRole>("licensed_agent");
  const [reHasLicense, setReHasLicense] = useState(true);
  const [reLicenseState, setReLicenseState] = useState("");
  const [reLicenseNumber, setReLicenseNumber] = useState("");
  // Finance specifics
  const [finRole, setFinRole] = useState<FinanceRole>("mortgage_broker");
  const [finHasLicense, setFinHasLicense] = useState(true);
  const [finLicenseType, setFinLicenseType] = useState("");
  const [finLicenseNumber, setFinLicenseNumber] = useState("");
  // Trades / professional (optional license)
  const [tradeHasLicense, setTradeHasLicense] = useState(false);
  const [tradeLicenseType, setTradeLicenseType] = useState("");
  const [tradeLicenseNumber, setTradeLicenseNumber] = useState("");
  const [proHasLicense, setProHasLicense] = useState(false);
  const [proLicenseType, setProLicenseType] = useState("");
  const [proLicenseNumber, setProLicenseNumber] = useState("");

  // No-ID lead
  const [whatTheyWant, setWhatTheyWant] = useState("");

  // Returning sign-in
  const [signEmail, setSignEmail] = useState("");
  const [signPassword, setSignPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  /** forgot | verify | temp_shown | link_reset */
  const [forgotStep, setForgotStep] = useState<
    "email" | "verify" | "temp" | "link"
  >("email");
  const [phoneLast4, setPhoneLast4] = useState("");
  const [phoneHint, setPhoneHint] = useState<string | null>(null);
  const [carrier, setCarrier] = useState("");
  const [carriers, setCarriers] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [challengeId, setChallengeId] = useState("");
  const [recoveryMethod, setRecoveryMethod] = useState<string>("");
  const [verifyCode, setVerifyCode] = useState("");
  const [tempPassword, setTempPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [resetPassword, setResetPassword] = useState("");

  // Legal consent (required for register, nearby lead, sign-in)
  const [acceptLegal, setAcceptLegal] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [freeDemoIds, setFreeDemoIds] = useState<string[]>([]);
  /** Dev API allows many accounts per email; production enforces one person / one email */
  const [devMultiAccount, setDevMultiAccount] = useState(false);

  useEffect(() => {
    void apiFetch<{
      free?: { code: string }[];
      allowDuplicateAccounts?: boolean;
      mode?: string;
    }>("/api/access/demo-ids")
      .then((r) => {
        const codes = (r.free ?? []).map((f) => f.code);
        setFreeDemoIds(codes);
        setDevMultiAccount(Boolean(r.allowDuplicateAccounts));
      })
      .catch(() => {
        setFreeDemoIds([]);
        setDevMultiAccount(false);
      });
    void apiFetch<{ ok: boolean; carriers?: { value: string; label: string }[] }>(
      "/api/access/carriers",
    )
      .then((r) => setCarriers(r.carriers ?? []))
      .catch(() => setCarriers([]));
  }, [path, step, success]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const q = new URLSearchParams(window.location.search);
    const r = q.get("reset");
    if (r) {
      setResetToken(r);
      setPath("returning");
      setForgotMode(true);
      setForgotStep("link");
    }
  }, []);

  function requireLegal(): boolean {
    if (!acceptLegal) {
      setError(
        "Please accept the Privacy Policy and Terms of Use to continue.",
      );
      return false;
    }
    return true;
  }

  function resetMessages() {
    setError(null);
    setSuccess(null);
  }

  async function checkUniqueId(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();
    setChecking(true);
    try {
      const res = await apiFetch<ValidateUniqueIdResponse>(
        "/api/access/validate-id",
        {
          method: "POST",
          body: JSON.stringify({ uniqueId }),
        },
      );
      setIdCheck(res);
      if (res.valid) {
        if (res.industryHint) setIndustry(res.industryHint);
        setStep(2);
      } else {
        // Already claimed → guide to email/password sign-in (Unique ID is not daily login)
        const alreadyUsed =
          /already used|already linked|sign in with email/i.test(
            res.message ?? "",
          );
        setError(res.message);
        if (alreadyUsed) {
          setSuccess(
            "This Unique ID already created an account. Use “I already have an account — sign in” with your email and password (not the Unique ID).",
          );
        }
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not check that ID. Is the API running?",
      );
    } finally {
      setChecking(false);
    }
  }

  function buildCompanyProfile(): CompanyProfile | undefined {
    if (industry === "real_estate") {
      const isWholesalerOrNonLicensed =
        reRole === "wholesaler" || reRole === "investor" || !reHasLicense;
      return {
        realEstate: {
          role: reRole,
          hasStateLicense: reHasLicense,
          licenseState: reHasLicense
            ? reLicenseState || state || undefined
            : undefined,
          licenseNumber: reHasLicense ? reLicenseNumber || undefined : undefined,
          // Wholesalers: send deals in; outbound referrals to them only last resort
          outboundReferralPolicy: isWholesalerOrNonLicensed
            ? "last_resort_only"
            : "standard",
        },
      };
    }
    if (industry === "finance") {
      const needsLicense =
        finRole === "mortgage_broker" ||
        finRole === "loan_officer" ||
        finRole === "other_licensed";
      return {
        finance: {
          role: finRole,
          hasLicense: needsLicense ? finHasLicense : false,
          licenseType: finHasLicense
            ? finLicenseType || undefined
            : undefined,
          licenseNumber: finHasLicense
            ? finLicenseNumber || undefined
            : undefined,
        },
      };
    }
    if (industry === "trades") {
      return {
        trades: {
          hasTradeLicense: tradeHasLicense,
          licenseType: tradeHasLicense ? tradeLicenseType || undefined : undefined,
          licenseNumber: tradeHasLicense
            ? tradeLicenseNumber || undefined
            : undefined,
        },
      };
    }
    if (industry === "professional_services") {
      return {
        professional: {
          hasLicense: proHasLicense,
          licenseType: proHasLicense ? proLicenseType || undefined : undefined,
          licenseNumber: proHasLicense
            ? proLicenseNumber || undefined
            : undefined,
        },
      };
    }
    return undefined;
  }

  async function register(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();
    if (!requireLegal()) return;
    if (!fullName.trim()) {
      setError("Enter your name.");
      return;
    }
    if (!email.trim()) {
      setError("Enter your email.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!uniqueId.trim()) {
      setError("Unique ID is missing. Go back and enter a free Unique ID.");
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch<RegisterWithIdResponse>("/api/access/register", {
        method: "POST",
        body: JSON.stringify({
          uniqueId: uniqueId.trim().toUpperCase(),
          fullName: fullName.trim(),
          email: email.trim(),
          phone,
          mobileCarrier: carrier || undefined,
          address: address || undefined,
          city,
          state,
          zip: zip || undefined,
          password,
          industry,
          preferences,
          companyName: companyName || undefined,
          website: website || undefined,
          calendarLink: calendarLink || undefined,
          availabilityNotes: availabilityNotes || undefined,
          companyProfile: buildCompanyProfile(),
        }),
      });
      if (!res.ok || !res.sessionToken) {
        setError(res.message || "Could not create account.");
        return;
      }
      saveSession(res.sessionToken, {
        fullName: res.fullName ?? fullName,
        industry: res.industry ?? industry,
        email,
        uniqueId: res.uniqueId ?? uniqueId.toUpperCase(),
        tier: res.tier,
        canIssueIds: res.canIssueIds,
        calendarLink: calendarLink || undefined,
        address: address || undefined,
        city: city || undefined,
        state: state || undefined,
        zip: zip || undefined,
      });
      // Optional setup: offer onboard AI (not required)
      router.push("/setup?from=register");
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Could not create account.";
      // Already-used ID → steer to sign-in
      if (/already used|already exists|sign in/i.test(msg)) {
        setError(msg);
        setSuccess(
          "If you already created this account, go back and choose “I already have an account — sign in”.",
        );
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitNearbyLead(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();
    if (!requireLegal()) return;
    setBusy(true);
    try {
      const res = await apiFetch<NearbyLeadResponse>("/api/access/nearby-lead", {
        method: "POST",
        body: JSON.stringify({
          fullName,
          email,
          phone,
          city,
          state,
          zip,
          whatTheyWant,
          industryInterest: industry,
        }),
      });
      if (!res.ok) {
        setError(res.message);
        return;
      }
      setSuccess(res.message);
      setPath("choose");
      setStep(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save your request.");
    } finally {
      setBusy(false);
    }
  }

  async function doSignIn(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();
    if (!requireLegal()) return;
    setBusy(true);
    try {
      const res = await apiFetch<
        SignInResponse & { needsTotp?: boolean }
      >("/api/access/sign-in", {
        method: "POST",
        body: JSON.stringify({
          email: signEmail,
          password: signPassword,
          totpCode: totpCode || undefined,
        }),
      });
      if (!res.ok || !res.sessionToken) {
        setError(res.message);
        if (res.needsTotp) setNeedsTotp(true);
        return;
      }
      saveSession(res.sessionToken, {
        fullName: res.fullName ?? "",
        industry: res.industry ?? "general",
        email: signEmail,
        uniqueId: res.uniqueId,
        tier: res.tier as number | undefined,
        canIssueIds: res.canIssueIds,
      });
      // Optional setup: offer onboard AI (skippable; may auto-skip if user opted out)
      router.push("/setup?from=signin");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not sign in.";
      setError(message);
      if (/authenticator|6-digit/i.test(message)) setNeedsTotp(true);
    } finally {
      setBusy(false);
    }
  }

  async function doForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();
    if (!signEmail.trim()) {
      setError("Enter the email on your account to recover the password.");
      return;
    }
    setBusy(true);
    try {
      const res = await apiFetch<{
        ok: boolean;
        message: string;
        step?: string;
        challengeId?: string;
        method?: string;
        phoneHint?: string | null;
        deliveryNote?: string;
        needsCarrier?: boolean;
        carriers?: { value: string; label: string }[];
        resetToken?: string;
        resetPath?: string;
        devResetPath?: string;
      }>("/api/access/forgot-password", {
        method: "POST",
        body: JSON.stringify({
          email: signEmail.trim(),
          phoneLast4: phoneLast4 || undefined,
          carrier: carrier || undefined,
        }),
      });

      // apiFetch only returns on HTTP 2xx; ok:false with 200 is rare
      if (res.ok === false) {
        setError(res.message || "Could not start password recovery.");
        if (res.phoneHint) setPhoneHint(res.phoneHint);
        if (res.carriers?.length) setCarriers(res.carriers);
        return;
      }
      if (res.carriers?.length) setCarriers(res.carriers);
      if (res.phoneHint) setPhoneHint(res.phoneHint);

      // Phone-style 2FA step
      if (res.step === "verify" && res.challengeId) {
        setChallengeId(res.challengeId);
        setRecoveryMethod(res.method || "phone_otp");
        setForgotStep("verify");
        setVerifyCode("");
        setSuccess(res.message || "Enter the 6-digit code from your phone.");
        return;
      }

      // Legacy link-token fallback
      const token =
        res.resetToken ||
        res.resetPath?.match(/reset=([a-f0-9]+)/i)?.[1] ||
        res.devResetPath?.match(/reset=([a-f0-9]+)/i)?.[1] ||
        "";
      if (token) {
        setResetToken(token);
        setForgotStep("link");
        setSuccess(res.message);
        return;
      }

      setSuccess(
        res.message ||
          "If that email is on an account, follow the next instructions shown here.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start reset.");
    } finally {
      setBusy(false);
    }
  }

  async function doVerifyRecovery(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();
    setBusy(true);
    try {
      const res = await apiFetch<{
        ok: boolean;
        message: string;
        temporaryPassword?: string;
        email?: string;
        emailed?: boolean;
        smsSent?: boolean;
      }>("/api/access/forgot-password/verify", {
        method: "POST",
        body: JSON.stringify({
          challengeId,
          code: verifyCode,
        }),
      });
      if (!res.ok) {
        setError(res.message);
        return;
      }
      if (res.temporaryPassword) {
        setTempPassword(res.temporaryPassword);
        setForgotStep("temp");
        if (res.email) setSignEmail(res.email);
      }
      setSuccess(res.message);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not verify the code.",
      );
    } finally {
      setBusy(false);
    }
  }

  function clearForgot() {
    setForgotMode(false);
    setForgotStep("email");
    setChallengeId("");
    setVerifyCode("");
    setTempPassword("");
    setPhoneLast4("");
    setPhoneHint(null);
    setRecoveryMethod("");
    setResetToken("");
    setResetPassword("");
  }

  async function doResetPassword(e: React.FormEvent) {
    e.preventDefault();
    resetMessages();
    setBusy(true);
    try {
      const res = await apiFetch<{ ok: boolean; message: string }>(
        "/api/access/reset-password",
        {
          method: "POST",
          body: JSON.stringify({
            token: resetToken,
            password: resetPassword,
          }),
        },
      );
      if (!res.ok) {
        setError(res.message);
        return;
      }
      setSuccess(res.message);
      setResetToken("");
      setResetPassword("");
      setForgotMode(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not reset password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-[calc(100vh-8rem)] max-w-lg flex-col justify-center px-4 py-10">
      <div className="mb-6 flex flex-col items-center text-center">
        <Link
          href="/"
          className="mx-auto inline-flex items-center justify-center rounded-full outline-none transition hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
          aria-label="Back to home"
        >
          <BrandLogo size="lg" withText={false} href={null} />
        </Link>
        <h1 className="mt-4 text-2xl font-semibold text-title">Join or sign in</h1>
        <p className="mt-2 max-w-md text-sm text-body">
          <strong>New?</strong> Use a Unique ID once to create your account.{" "}
          <strong>Returning?</strong> Sign in with email and password only — the
          ID stays in the background for tracking. IDs come from someone above
          you (not Tier 4).
        </p>
        {devMultiAccount && (
          <p className="mt-3 max-w-md rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-left text-xs text-amber-950">
            <strong>Developer mode:</strong> prefer one account and use{" "}
            <strong>Account views</strong> (industry + tier preview) after
            sign-in. You may still create multiple accounts with the same email
            if needed. Production locks one account per email and per person.
          </p>
        )}
      </div>

      {success && (
        <div className="mb-4 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-900">
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="card p-5 sm:p-6">
        {path !== "choose" && path !== "has_id" && (
          <LegalConsent checked={acceptLegal} onChange={setAcceptLegal} />
        )}
        {path === "has_id" && step === 2 && (
          <LegalConsent checked={acceptLegal} onChange={setAcceptLegal} />
        )}
        {path === "has_id" && step === 1 && null}

        {/* Path chooser */}
        {path === "choose" && (
          <div className="space-y-3">
            <LegalConsent checked={acceptLegal} onChange={setAcceptLegal} />
            <p className="text-sm text-body">How are you joining today?</p>
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => {
                resetMessages();
                setPath("has_id");
                setStep(1);
              }}
            >
              I have a Unique ID
            </button>
            <button
              type="button"
              className="btn-ghost w-full"
              onClick={() => {
                resetMessages();
                setPath("no_id");
              }}
            >
              I don’t have a Unique ID yet
            </button>
            <button
              type="button"
              className="btn-ghost w-full"
              onClick={() => {
                resetMessages();
                setPath("returning");
              }}
            >
              I already have an account — sign in
            </button>
            <p className="pt-2 text-xs text-[var(--faint)]">
              No Unique ID means you are not in the network yet. Someone nearby
              can meet you, help with what you need, then an admin can issue your
              ID.
            </p>
          </div>
        )}

        {/* Path: has Unique ID — Part 1 */}
        {path === "has_id" && step === 1 && (
          <form onSubmit={checkUniqueId} className="space-y-4">
            <div className="flex items-center justify-between text-xs text-[var(--faint)]">
              <span>Part 1 of 2</span>
              <button
                type="button"
                className="text-[var(--accent)]"
                onClick={() => setPath("choose")}
              >
                ← Back
              </button>
            </div>
            <h2 className="text-lg font-semibold text-title">
              Unique ID to create your account
            </h2>
            <p className="text-sm text-body">
              Used <strong>once</strong> to create your account — not for daily
              login. After setup you use <strong>email and password</strong>.
              If this ID was already used, go back and choose{" "}
              <strong>I already have an account — sign in</strong>.
            </p>
            <label className="block text-left text-sm">
              <span className="mb-1 block text-[var(--muted)]">Unique ID</span>
              <input
                className="input-field font-mono uppercase"
                value={uniqueId}
                onChange={(e) => setUniqueId(e.target.value)}
                placeholder="e.g. VIRT-MIAMI-001"
                required
                autoFocus
              />
            </label>
            <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-3 text-[11px] text-[var(--faint)]">
              <p className="mb-1.5 font-semibold text-[var(--muted)]">
                Free demo IDs (use once each)
              </p>
              {freeDemoIds.length > 0 ? (
                <ul className="flex flex-wrap gap-1.5">
                  {freeDemoIds.slice(0, 8).map((code) => (
                    <li key={code}>
                      <button
                        type="button"
                        className="rounded-md border border-[var(--border)] bg-white px-2 py-1 font-mono text-[11px] font-semibold text-[var(--text)] hover:border-[var(--text)]"
                        onClick={() => setUniqueId(code)}
                      >
                        {code}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>
                  No free demo IDs listed — restart the API, or sign in if you
                  already created an account.
                </p>
              )}
              <p className="mt-1.5">
                Already used an ID? Go back →{" "}
                <strong>I already have an account — sign in</strong>.
              </p>
            </div>
            <button type="submit" className="btn-primary w-full" disabled={checking}>
              {checking ? "Checking…" : "Continue"}
            </button>
          </form>
        )}

        {/* Path: has Unique ID — Part 2 */}
        {path === "has_id" && step === 2 && (
          <form onSubmit={register} className="space-y-3">
            <div className="flex items-center justify-between text-xs text-[var(--faint)]">
              <span>Part 2 of 2</span>
              <button
                type="button"
                className="text-[var(--accent)]"
                onClick={() => setStep(1)}
              >
                ← Change ID
              </button>
            </div>
            {idCheck?.valid && (
              <p className="rounded-lg bg-[var(--accent-soft)] px-3 py-2 text-sm text-[var(--accent)]">
                {idCheck.message}
              </p>
            )}
            <h2 className="text-lg font-semibold text-title">Your information</h2>
            <p className="text-[11px] leading-relaxed text-[var(--faint)]">
              Company and location first — customer-facing referrals will use
              location (and later calendar availability) alongside pricing.
            </p>

            {/* 1. Industry */}
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Industry *</span>
              <select
                className="input-field"
                value={industry}
                onChange={(e) =>
                  setIndustry(e.target.value as IndustryTemplate)
                }
              >
                {INDUSTRY_TEMPLATES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>

            {/* 2. Company name */}
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Company name</span>
              <input
                className="input-field"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                autoComplete="organization"
                placeholder="Business or DBA (if any)"
              />
            </label>

            {/* 3. Website */}
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Website</span>
              <input
                className="input-field"
                type="text"
                inputMode="url"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                autoComplete="url"
                placeholder="https://yourcompany.com"
              />
            </label>

            {/* 4. Company license (if any) — industry-aware */}
            <div className="space-y-3 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">
                Company license (if any)
              </p>

              {industry === "real_estate" && (
                <>
                  <label className="block text-sm">
                    <span className="mb-1 block text-[var(--muted)]">Role</span>
                    <select
                      className="input-field"
                      value={reRole}
                      onChange={(e) => {
                        const role = e.target.value as RealEstateRole;
                        setReRole(role);
                        if (role === "wholesaler" || role === "investor") {
                          setReHasLicense(false);
                        } else if (
                          role === "licensed_agent" ||
                          role === "broker"
                        ) {
                          setReHasLicense(true);
                        }
                      }}
                    >
                      <option value="licensed_agent">Licensed agent</option>
                      <option value="broker">Broker</option>
                      <option value="wholesaler">
                        Wholesaler (often no RE license)
                      </option>
                      <option value="investor">Investor</option>
                      <option value="other">Other</option>
                    </select>
                  </label>
                  <label className="flex cursor-pointer items-start gap-2 text-sm text-body">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={reHasLicense}
                      onChange={(e) => setReHasLicense(e.target.checked)}
                    />
                    <span>
                      Company / agent has a <strong>state real estate license</strong>
                    </span>
                  </label>
                  {reHasLicense && (
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block text-sm">
                        <span className="mb-1 block text-[var(--muted)]">
                          License state
                        </span>
                        <input
                          className="input-field"
                          value={reLicenseState}
                          onChange={(e) => setReLicenseState(e.target.value)}
                          placeholder="e.g. FL"
                        />
                      </label>
                      <label className="block text-sm">
                        <span className="mb-1 block text-[var(--muted)]">
                          License number
                        </span>
                        <input
                          className="input-field"
                          value={reLicenseNumber}
                          onChange={(e) => setReLicenseNumber(e.target.value)}
                        />
                      </label>
                    </div>
                  )}
                  {(reRole === "wholesaler" ||
                    reRole === "investor" ||
                    !reHasLicense) && (
                    <p className="text-[11px] leading-relaxed text-[var(--faint)]">
                      Wholesalers can <strong>send deals in</strong>. Outbound
                      referrals <strong>to</strong> them only as a{" "}
                      <strong>last resort</strong>.
                    </p>
                  )}
                </>
              )}

              {industry === "finance" && (
                <>
                  <label className="block text-sm">
                    <span className="mb-1 block text-[var(--muted)]">Role</span>
                    <select
                      className="input-field"
                      value={finRole}
                      onChange={(e) => {
                        const role = e.target.value as FinanceRole;
                        setFinRole(role);
                        if (role === "not_licensed") setFinHasLicense(false);
                        else if (
                          role === "mortgage_broker" ||
                          role === "loan_officer"
                        ) {
                          setFinHasLicense(true);
                        }
                      }}
                    >
                      <option value="mortgage_broker">
                        Mortgage broker (license typically required)
                      </option>
                      <option value="loan_officer">
                        Loan officer (license typically required)
                      </option>
                      <option value="advisor">Advisor / planner</option>
                      <option value="other_licensed">Other licensed role</option>
                      <option value="not_licensed">
                        Not licensed (role does not require it)
                      </option>
                    </select>
                  </label>
                  {finRole !== "not_licensed" && (
                    <>
                      <label className="flex cursor-pointer items-start gap-2 text-sm text-body">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={finHasLicense}
                          onChange={(e) => setFinHasLicense(e.target.checked)}
                        />
                        <span>
                          Company has a current{" "}
                          <strong>finance / mortgage license</strong>
                        </span>
                      </label>
                      {finHasLicense && (
                        <div className="grid grid-cols-2 gap-2">
                          <label className="block text-sm">
                            <span className="mb-1 block text-[var(--muted)]">
                              License type
                            </span>
                            <input
                              className="input-field"
                              value={finLicenseType}
                              onChange={(e) =>
                                setFinLicenseType(e.target.value)
                              }
                              placeholder="e.g. NMLS, state MLO"
                            />
                          </label>
                          <label className="block text-sm">
                            <span className="mb-1 block text-[var(--muted)]">
                              License / NMLS #
                            </span>
                            <input
                              className="input-field"
                              value={finLicenseNumber}
                              onChange={(e) =>
                                setFinLicenseNumber(e.target.value)
                              }
                            />
                          </label>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {industry === "trades" && (
                <>
                  <label className="flex cursor-pointer items-start gap-2 text-sm text-body">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={tradeHasLicense}
                      onChange={(e) => setTradeHasLicense(e.target.checked)}
                    />
                    <span>Company has a trade / contractor license</span>
                  </label>
                  {tradeHasLicense && (
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block text-sm">
                        <span className="mb-1 block text-[var(--muted)]">
                          License type
                        </span>
                        <input
                          className="input-field"
                          value={tradeLicenseType}
                          onChange={(e) => setTradeLicenseType(e.target.value)}
                          placeholder="e.g. GC, electrical"
                        />
                      </label>
                      <label className="block text-sm">
                        <span className="mb-1 block text-[var(--muted)]">
                          License number
                        </span>
                        <input
                          className="input-field"
                          value={tradeLicenseNumber}
                          onChange={(e) =>
                            setTradeLicenseNumber(e.target.value)
                          }
                        />
                      </label>
                    </div>
                  )}
                </>
              )}

              {industry === "professional_services" && (
                <>
                  <label className="flex cursor-pointer items-start gap-2 text-sm text-body">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={proHasLicense}
                      onChange={(e) => setProHasLicense(e.target.checked)}
                    />
                    <span>
                      Company has a professional license (e.g. bar, CPA)
                    </span>
                  </label>
                  {proHasLicense && (
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block text-sm">
                        <span className="mb-1 block text-[var(--muted)]">
                          License type
                        </span>
                        <input
                          className="input-field"
                          value={proLicenseType}
                          onChange={(e) => setProLicenseType(e.target.value)}
                        />
                      </label>
                      <label className="block text-sm">
                        <span className="mb-1 block text-[var(--muted)]">
                          License number
                        </span>
                        <input
                          className="input-field"
                          value={proLicenseNumber}
                          onChange={(e) => setProLicenseNumber(e.target.value)}
                        />
                      </label>
                    </div>
                  )}
                </>
              )}

              {(industry === "shipping" ||
                industry === "retail" ||
                industry === "general") && (
                <p className="text-[11px] text-[var(--faint)]">
                  No industry license required for this category. You can still
                  note credentials later in preferences.
                </p>
              )}
            </div>

            {/* 5. Their name */}
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Your name *</span>
              <input
                className="input-field"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="name"
              />
            </label>

            {/* 6–9. Location for referrals */}
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Address</span>
              <input
                className="input-field"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                autoComplete="street-address"
                placeholder="Street and suite"
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-sm">
                <span className="mb-1 block text-[var(--muted)]">City</span>
                <input
                  className="input-field"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  autoComplete="address-level2"
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block text-[var(--muted)]">State</span>
                <input
                  className="input-field"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  autoComplete="address-level1"
                  placeholder="e.g. FL"
                />
              </label>
            </div>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">ZIP code</span>
              <input
                className="input-field"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                autoComplete="postal-code"
                inputMode="numeric"
                placeholder="e.g. 33101"
              />
            </label>

            <div className="space-y-2 rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">
                Calendar (for receiving referrals)
              </p>
              <p className="text-[11px] text-body">
                If you receive deals, other Virtuous members use the calendar
                link on <strong>your account</strong> — not Zoom/Meet fields on
                each referral.
              </p>
              <label className="block text-sm">
                <span className="mb-1 block text-[var(--muted)]">
                  Calendar availability link
                </span>
                <input
                  className="input-field"
                  type="text"
                  inputMode="url"
                  value={calendarLink}
                  onChange={(e) => setCalendarLink(e.target.value)}
                  placeholder="https://calendly.com/… or booking page"
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block text-[var(--muted)]">
                  Availability notes
                </span>
                <input
                  className="input-field"
                  value={availabilityNotes}
                  onChange={(e) => setAvailabilityNotes(e.target.value)}
                  placeholder="e.g. Weekdays 9–5 ET"
                />
              </label>
            </div>

            {/* Account login fields */}
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Email *</span>
              <input
                id="register-email"
                name="email"
                type="email"
                className="input-field"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Phone</span>
              <input
                className="input-field"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">
                Mobile carrier (free password recovery texts)
              </span>
              <select
                className="input-field"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
              >
                <option value="">Select carrier…</option>
                {carriers.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Password *</span>
              <input
                id="register-password"
                name="password"
                type="password"
                className="input-field"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                placeholder="At least 8 characters"
              />
            </label>
            <p className="text-[11px] text-[var(--faint)]">
              Browser can offer <strong>Save password</strong> for next time.
              Location supports nearby / customer-facing referrals; calendar
              availability and pricing will build on this later.
            </p>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">
                Preferences (optional)
              </span>
              <textarea
                className="input-field"
                rows={2}
                value={preferences}
                onChange={(e) => setPreferences(e.target.value)}
                placeholder="Service areas, specialties, languages, typical hours…"
              />
            </label>
            <button type="submit" className="btn-primary w-full" disabled={busy}>
              {busy ? "Creating…" : "Create account & enter"}
            </button>
          </form>
        )}

        {/* Path: no Unique ID → nearby lead */}
        {path === "no_id" && (
          <form onSubmit={submitNearbyLead} className="space-y-3">
            <div className="flex items-center justify-between text-xs text-[var(--faint)]">
              <span>Meet someone first</span>
              <button
                type="button"
                className="text-[var(--accent)]"
                onClick={() => setPath("choose")}
              >
                ← Back
              </button>
            </div>
            <h2 className="text-lg font-semibold text-title">
              Request a nearby partner
            </h2>
            <p className="text-sm text-body">
              You will <strong>not</strong> get network access yet. A partner near
              you can reach out, meet you, help with what you want, and then an
              admin can give you a Unique ID.
            </p>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Full name *</span>
              <input
                className="input-field"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Email *</span>
              <input
                type="email"
                className="input-field"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Phone</span>
              <input
                className="input-field"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-sm">
                <span className="mb-1 block text-[var(--muted)]">City *</span>
                <input
                  className="input-field"
                  required
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block text-[var(--muted)]">State *</span>
                <input
                  className="input-field"
                  required
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                />
              </label>
            </div>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">ZIP</span>
              <input
                className="input-field"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">
                What do you need help with? *
              </span>
              <textarea
                className="input-field"
                required
                rows={3}
                value={whatTheyWant}
                onChange={(e) => setWhatTheyWant(e.target.value)}
                placeholder="e.g. Looking to buy a home in Miami, or need a contractor…"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Industry interest</span>
              <select
                className="input-field"
                value={industry}
                onChange={(e) =>
                  setIndustry(e.target.value as IndustryTemplate)
                }
              >
                {INDUSTRY_TEMPLATES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit" className="btn-primary w-full" disabled={busy}>
              {busy ? "Sending…" : "Request a meet — no access yet"}
            </button>
          </form>
        )}

        {/* Returning users */}
        {/* Forgot password — link-token path (legacy) */}
        {path === "returning" && forgotMode && forgotStep === "link" && resetToken ? (
          <form onSubmit={doResetPassword} className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--faint)]">
              New password
            </p>
            <h2 className="text-lg font-semibold text-title">
              Choose a new password
            </h2>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">New password</span>
              <input
                type="password"
                className="input-field"
                required
                minLength={8}
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <button type="submit" className="btn-primary w-full" disabled={busy}>
              {busy ? "Saving…" : "Save new password"}
            </button>
          </form>
        ) : null}

        {/* Forgot — temporary password shown once after phone 2FA */}
        {path === "returning" && forgotMode && forgotStep === "temp" ? (
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--faint)]">
              Temporary password
            </p>
            <h2 className="text-lg font-semibold text-title">
              You&apos;re verified — use this password
            </h2>
            <p className="text-sm text-body">
              Sign in with <strong>{signEmail || "your email"}</strong> and the
              temporary password below. Change it after you log in.
            </p>
            <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-soft)] p-4 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--faint)]">
                Temporary password (shown once)
              </p>
              <p className="mt-2 break-all font-mono text-lg font-bold tracking-wide text-[var(--text)]">
                {tempPassword}
              </p>
            </div>
            <button
              type="button"
              className="btn-primary w-full"
              onClick={() => {
                setSignPassword(tempPassword);
                clearForgot();
                setSuccess("Enter your email and the temporary password to sign in.");
              }}
            >
              Continue to sign in
            </button>
          </div>
        ) : null}

        {/* Forgot — enter 6-digit code from phone */}
        {path === "returning" && forgotMode && forgotStep === "verify" ? (
          <form onSubmit={doVerifyRecovery} className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--faint)]">
              Confirm on your phone
            </p>
            <h2 className="text-lg font-semibold text-title">
              Enter the 6-digit code
            </h2>
            <p className="text-sm text-body">
              {recoveryMethod === "totp"
                ? "Open your authenticator app on your phone (Google Authenticator, Authy, or Microsoft Authenticator) and enter the current 6-digit code."
                : phoneHint
                  ? `We sent a 6-digit code for verification${phoneHint ? ` (phone ${phoneHint})` : ""}. Enter it below. Then a temporary password will be issued.`
                  : "Enter the 6-digit code from your phone or recovery message."}
            </p>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">6-digit code</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                className="input-field tracking-widest"
                required
                value={verifyCode}
                onChange={(e) =>
                  setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                autoComplete="one-time-code"
                placeholder="123456"
              />
            </label>
            <button type="submit" className="btn-primary w-full" disabled={busy}>
              {busy ? "Checking…" : "Verify & get temporary password"}
            </button>
            <button
              type="button"
              className="w-full text-center text-xs text-[var(--muted)] underline"
              onClick={() => {
                setForgotStep("email");
                setChallengeId("");
                setVerifyCode("");
              }}
            >
              Start over
            </button>
          </form>
        ) : null}

        {/* Forgot — start: email + phone last 4 */}
        {path === "returning" && forgotMode && forgotStep === "email" ? (
          <form onSubmit={doForgotPassword} className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--faint)]">
              Forgot password
            </p>
            <h2 className="text-lg font-semibold text-title">
              Recover with phone-style 2FA
            </h2>
            <p className="text-sm text-body">
              Confirm with a <strong>6-digit code on your phone</strong>{" "}
              (authenticator or text/email). Then you get a{" "}
              <strong>temporary password</strong> to sign in.
            </p>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Email</span>
              <input
                type="email"
                className="input-field"
                required
                value={signEmail}
                onChange={(e) => setSignEmail(e.target.value)}
                autoComplete="username"
                autoFocus
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">
                Phone last 4 digits
                {phoneHint ? ` (on file: ${phoneHint})` : " (if phone is on your account)"}
              </span>
              <input
                type="text"
                inputMode="numeric"
                maxLength={4}
                className="input-field tracking-widest"
                value={phoneLast4}
                onChange={(e) =>
                  setPhoneLast4(e.target.value.replace(/\D/g, "").slice(0, 4))
                }
                placeholder="1234"
                autoComplete="tel-national"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">
                Mobile carrier (free text codes)
              </span>
              <select
                className="input-field"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
              >
                <option value="">Select carrier (optional if using authenticator)</option>
                {carriers.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <button type="submit" className="btn-primary w-full" disabled={busy}>
              {busy ? "Working…" : "Continue — confirm on phone"}
            </button>
            <button
              type="button"
              className="btn-ghost w-full text-sm"
              onClick={() => {
                resetMessages();
                clearForgot();
              }}
            >
              Cancel — back to sign in
            </button>
          </form>
        ) : null}

        {path === "returning" && !forgotMode ? (
          <form onSubmit={doSignIn} className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--faint)]">
              Returning member
            </p>
            <h2 className="text-lg font-semibold text-title">Sign in</h2>
            <p className="text-sm text-body">
              Email and password only. Your Unique ID stays on the account for
              tracking — you do not type it here.
            </p>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Email</span>
              <input
                id="signin-email"
                name="email"
                type="email"
                className="input-field"
                required
                value={signEmail}
                onChange={(e) => setSignEmail(e.target.value)}
                autoComplete="username"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-[var(--muted)]">Password</span>
              <input
                id="signin-password"
                name="password"
                type="password"
                className="input-field"
                required
                value={signPassword}
                onChange={(e) => setSignPassword(e.target.value)}
                autoComplete="current-password"
              />
            </label>
            {needsTotp ? (
              <label className="block text-sm">
                <span className="mb-1 block text-[var(--muted)]">
                  Authenticator code
                </span>
                <input
                  className="input-field"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="6 digits from your phone app"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                />
              </label>
            ) : null}
            <button type="submit" className="btn-primary w-full" disabled={busy}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <button
              type="button"
              className="btn-ghost w-full"
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                resetMessages();
                setForgotMode(true);
                setForgotStep("email");
                setTempPassword("");
                setChallengeId("");
                setVerifyCode("");
                setResetToken("");
                setRecoveryMethod("");
              }}
            >
              Forgot password?
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}

function LegalConsent({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="mb-4 flex cursor-pointer items-start gap-2 rounded-xl border border-[var(--border)] bg-[var(--bg)] p-3 text-left text-xs text-body">
      <input
        type="checkbox"
        className="mt-0.5"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span>
        I agree to the{" "}
        <Link href="/privacy" className="text-[var(--accent)] hover:underline">
          Privacy Policy
        </Link>{" "}
        and{" "}
        <Link href="/terms" className="text-[var(--accent)] hover:underline">
          Terms of Use
        </Link>
        , including AI safety rules (no illegal or harmful use of the helper).
      </span>
    </label>
  );
}
